import React from "react";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocAlert,
  DocLink,
  DocNavFooter,
  DocStepIndicator,
  DocCard,
} from "@/components/docs";
import { CodeBlock } from "@/components/docs/api";
import { docsColors, docsComponents } from "@/lib/docs-theme";
import { KeyIcon, ShieldExclamationIcon, LockClosedIcon } from "@heroicons/react/24/outline";

export default function ApiAuthenticationPage() {
  const breadcrumbs = [
    { label: "Documentation", href: "/docs" },
    { label: "API Reference", href: "/docs/api" },
    { label: "Authentication" },
  ];

  return (
    <DocLayout
      title="API Authentication - Uptime Monitor Documentation"
      description="Learn how to authenticate with the Uptime Monitor API using Bearer tokens"
      canonical="https://uptime-monitor.io/docs/api/authentication"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="Authentication"
        description="Use API keys with Bearer authentication to access the Uptime Monitor API."
      />

      {/* Generate API Key */}
      <section className="mb-8">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-4`}>
          Generate API Key
        </h2>

        <p className={`${docsColors.text.body} mb-4`}>
          Go to{" "}
          <DocLink href="https://uptime-monitor.io/dashboard/settings" external>
            Settings
          </DocLink>{" "}
          → API section and click &ldquo;Generate New API Key&rdquo;. Copy it immediately - you
          won&apos;t see it again.
        </p>
      </section>

      {/* Using API Keys */}
      <section className="mb-8">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-4`}>Using API Keys</h2>

        <p className={`${docsColors.text.body} mb-4`}>
          Include your API key in the <code>Authorization</code> header:
        </p>

        <CodeBlock language="bash">
          {`curl -X GET https://api.uptime-monitor.io/api/monitors \\
  -H "Authorization: Bearer um_your_api_key_here"`}
        </CodeBlock>
      </section>

      {/* Security */}
      <section className="mb-8">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-4`}>Security</h2>

        <DocAlert variant="warning">
          <p>
            <strong>Keep API keys secure:</strong> Store them in environment variables, never in
            source code. Use server-side only - API keys provide full account access.
          </p>
        </DocAlert>
      </section>

      <DocNavFooter
        prev={{ href: "/docs/api", text: "API Overview" }}
        next={{ href: "/docs/api/contacts", text: "Contacts API" }}
        className="mt-12"
      />
    </DocLayout>
  );
}
